import React, { useEffect, useMemo } from 'react';
import { mixed, object } from 'yup';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import type { ChooseOfferFormData, Offer } from '@jwp/ott-common/types/checkout';
import { modalURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import useOffers from '@jwp/ott-hooks-react/src/useOffers';
import useForm from '@jwp/ott-hooks-react/src/useForm';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { getModule } from '@jwp/ott-common/src/modules/container';
import AccountController from '@jwp/ott-common/src/controllers/AccountController';
import { createURL } from '@jwp/ott-common/src/utils/urlFormatting';

import ChooseOfferForm from '../../../components/ChooseOfferForm/ChooseOfferForm';
import LoadingOverlay from '../../../components/LoadingOverlay/LoadingOverlay';
import useQueryParam from '../../../hooks/useQueryParam';

export type OfferTabs = { offerGroupId: string; label?: string; offers: Offer[] }[];

// This is to support both localised and non-localised offer deeplinks, which is Cleeng specific
// @todo: move all localised logics away and into cleeng services
const matchesDeepLinkedOffer = (offer: Offer, offerId: string | null) => {
  return !!offerId && (offer.offerId === offerId || (!!offer.offerCountry && offer.offerId === `${offerId}_${offer.offerCountry}`));
};

const ChooseOffer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('account');
  const isSwitch = useQueryParam('u') === 'upgrade-subscription';
  const preselectedOfferId = useQueryParam('offer');
  const preselectedOfferGroup = useQueryParam('offer-group');
  const isPendingOffer = useAccountStore(({ pendingOffer }) => ({ isPendingOffer: !!pendingOffer }));

  const { isLoading, offers, chooseOffer, switchSubscription } = useOffers();

  const checkoutUrl = modalURLFromLocation(location, 'checkout');
  const upgradePendingUrl = modalURLFromLocation(location, 'upgrade-subscription-pending');
  const upgradeSuccessUrl = modalURLFromLocation(location, 'upgrade-subscription-success');
  const upgradeErrorUrl = modalURLFromLocation(location, 'upgrade-subscription-error');

  const { values, errors, submitting, setValue, handleSubmit, handleChange } = useForm<ChooseOfferFormData>({
    initialValues: { selectedOfferId: undefined, selectedOfferTab: undefined },
    validationSchema: object().shape({
      selectedOfferId: mixed<string>().required(t('choose_offer.field_required')),
      selectedOfferTab: mixed<number>().required(t('choose_offer.field_required')),
    }),
    onSubmit: async ({ selectedOfferId }: ChooseOfferFormData) => {
      if (!selectedOfferId) return;

      const offer = offers.find(({ offerId }) => offerId === selectedOfferId);

      if (!offer) return;

      // offer should be bought via an external site, redirect the user to the `offerUrl`
      if (offer?.offerUrl) {
        const token = await getModule(AccountController).getAuthData();
        window.location.href = createURL(offer.offerUrl, {
          returnUrl: createURL(window.location.origin, { u: 'waiting-for-payment', type: offer.type, offerId: offer.offerId }),
          errorUrl: createURL(window.location.origin, { u: 'payment-error' }),
          cancelUrl: createURL(window.location.origin, { u: 'payment-cancelled' }),
          token: token?.jwt,
        });

        // the redirect may take some time, block the ui by keeping the form in the submitting state for 5 seconds in
        // case the user presses the back button i.c.w. https://web.dev/bfcache/
        await new Promise((resolve) => setTimeout(resolve, 5000));
        return;
      }

      await chooseOffer.mutateAsync(offer);

      if (isSwitch) {
        return await switchSubscription.mutateAsync();
      }
    },
    onSubmitSuccess: async () => {
      if (isSwitch && isPendingOffer) return navigate(upgradePendingUrl);
      if (isSwitch) return navigate(upgradeSuccessUrl);

      navigate(checkoutUrl);
    },
    onSubmitError: () => navigate(upgradeErrorUrl),
  });

  const offerTabs = useMemo(
    () =>
      offers.reduce<OfferTabs>((tabs, offer, index) => {
        const { id, label } = offer.offerGroup || {};
        const tab = tabs.find((tab) => tab.offerGroupId === id);

        if (tab) {
          tab.offers.push(offer);
        } else {
          tabs.push({ offerGroupId: id || index.toString(), label, offers: [offer] });
        }
        return tabs;
      }, [] as OfferTabs),
    [offers],
  );

  useEffect(() => {
    // Once offers are loaded, preselect first, or preselected (deeplinked) tab
    if (isLoading || !offers.length || values.selectedOfferTab !== undefined) return;

    const preselectedTabIndex = preselectedOfferId
      ? offerTabs.findIndex((tab) => tab.offers.some((offer) => matchesDeepLinkedOffer(offer, preselectedOfferId)))
      : preselectedOfferGroup
      ? offerTabs.findIndex((tab) => tab.label?.toLowerCase() === preselectedOfferGroup.toLowerCase())
      : -1;

    setValue('selectedOfferTab', Math.max(0, preselectedTabIndex));
  }, [isLoading, offers.length, values.selectedOfferTab, setValue, preselectedOfferId, preselectedOfferGroup, offerTabs]);

  useEffect(() => {
    // Select first offer when offer tab changes
    if (isLoading || !offers.length || values.selectedOfferTab === undefined) return;

    const selectedOffers = offerTabs[values.selectedOfferTab]?.offers || [];
    const selectedOffer = selectedOffers.find((offer) => matchesDeepLinkedOffer(offer, preselectedOfferId)) || selectedOffers[0];
    const selectedOfferId = selectedOffer?.offerId;

    if (values.selectedOfferId !== selectedOfferId) {
      setValue('selectedOfferId', selectedOfferId);
      return;
    }

    // Auto submit for preselected
    if (selectedOffer && matchesDeepLinkedOffer(selectedOffer, preselectedOfferId) && !submitting && !errors) {
      handleSubmit();
    }
  }, [isLoading, offers.length, values.selectedOfferTab, values.selectedOfferId, setValue, offerTabs, preselectedOfferId, submitting, errors, handleSubmit]);

  // loading state
  if (isLoading) {
    return (
      <div style={{ height: 300 }}>
        <LoadingOverlay inline />
      </div>
    );
  }

  return <ChooseOfferForm onSubmit={handleSubmit} onChange={handleChange} values={values} errors={errors} submitting={submitting} offerTabs={offerTabs} />;
};

export default ChooseOffer;
